import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

// ✅ Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ✅ Error handler
const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ LeadIndustry Interface
export interface LeadIndustry {
  LeadIndustry_id?: string;
  LeadIndustry_name: string;
  Industry_status: string;
  created_at?: string;
  client?: {
    client_id: string;
  };
}

// ✅ Create Lead Industry
export const createLeadIndustry = async (
  data: { LeadIndustry_name: string; Industry_status?: string }
): Promise<LeadIndustry | null> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadIndustry_name", data.LeadIndustry_name);
    form.append("Industry_status", data.Industry_status || "Active");

    const response = await axios.post(
      `${API_BASE_PATH}/leadIndustry_create/`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Lead Business created successfully!");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Lead Industry";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Update Lead Industry
export const updateLeadIndustry = async (
  leadIndustryId: string,
  data: { LeadIndustry_name: string; Industry_status?: string }
): Promise<boolean> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadIndustry_name", data.LeadIndustry_name);
    form.append("Industry_status", data.Industry_status || "Active");

    await axios.put(
      `${API_BASE_PATH}/leadIndustry/${leadIndustryId}/update`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Lead Business updated successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
    error?.response?.data?.details || error?.response?.data?.message || "Failed to update Lead Industry";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ Delete Lead Industry
export const deleteLeadIndustry = async (
  leadIndustryId: string
): Promise<boolean> => {
  try {
    await axios.delete(
      `${API_BASE_PATH}/leadIndustry/${leadIndustryId}/delete`,
      {
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead Business deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete Lead Industry";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ Fetch All Lead Industries
export const fetchAllLeadIndustries = async (): Promise<LeadIndustry[]> => {
  try {
    const response = await axios.get(
      `${API_BASE_PATH}/leadIndustry_get_all_table_data`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Industries";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Fetch All Lead Industries
export const fetchAllLeadIndustriesActive = async (): Promise<LeadIndustry[]> => {
  try {
    const response = await axios.get(
      `${API_BASE_PATH}/leadIndustry_get_all_table_data/active`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Sources";
    toast.error(errorMessage);
    return [];
  }
};


/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Lead Industries – Excel
export const exportLeadIndustryExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/leadindustry_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead industries exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead industries (Excel)");
    throw error;
  }
};

// ✅ Export all Lead Industries – PDF
export const exportLeadIndustryPDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/leadindustry_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead industries exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead industries (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Lead Industries – Excel
export const exportSelectedLeadIndustryExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/leadindustry_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead industries exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead industries (Excel)");
    throw error;
  }
};

// ✅ Export selected Lead Industries – PDF
export const exportSelectedLeadIndustryPDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/leadindustry_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead industries exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead industries (PDF)");
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Lead Industries
export const deleteLeadIndustryBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    await axios.delete(
      `${API_BASE_PATH}/leadindustry_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    //toast.success("Lead industries deleted successfully");
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete lead industries.");
    } else {
      handleAxiosError(error, "Failed to delete lead industries");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface LeadIndustryImportResult {
  message: string;
  added: {
    lead_industry_name: string;
    lead_industry_status: string;
  }[];
  skipped: {
    row: number;
    lead_industry_name: string;
    reason: string;
  }[];
}

// ✅ Import Lead Industry Excel
export const importLeadIndustryExcel = async (
  file: File
): Promise<LeadIndustryImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/leadindustry_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Lead industry import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import lead industries");
    throw error;
  }
};
